<template>
  <div class="grid grid-rows-[1fr_auto] h-full overflow-y-hidden md:overflow-y-auto w-full">
    <div class="flex flex-col w-full h-full overflow-y-hidden md:overflow-y-auto">
      <div v-if="!isReorderMode && hasAnyServices"
           class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 md:px-0 mb-3">
        <IconField class="w-full md:max-w-[300px]">
          <InputIcon class="pi pi-search" />
          <InputText size="small" v-model="searchValue" placeholder="חיפוש שירות" class="w-full" />
        </IconField>
        <div class="w-full flex justify-center md:justify-start gap-2">
          <Button
            v-if="!onlyAllowedToChoose"
            size="small"
            severity="secondary"
            outlined
            icon="pi pi-upload"
            label="ייבוא שירותים"
            class="w-full md:w-max"
            @click="openImportServices" />
          <Button
            v-if="allServices.length > 1"
            size="small"
            severity="secondary"
            outlined
            icon="pi pi-bars"
            label="סידור שירותים"
            class="w-full md:w-max"
            @click="enterReorderMode" />
        </div>
      </div>

      <div v-if="!isReorderMode && shouldShowCategoryChips"
           class="flex w-full items-center gap-2 overflow-x-auto px-4 md:px-0 mb-3 flex-none">
        <div v-for="cat of categoryChips" :key="cat"
             :class="['flex-none rounded-full border px-4 h-8 flex items-center cursor-pointer transition-colors whitespace-nowrap text-sm ',
                      selectedCategory === cat
                        ? 'bg-primary border-primary text-white'
                        : 'border-gray-300 text-gray-600 hover:border-gray-400']"
             @click="selectedCategory = cat">
          {{ cat === ALL_CATEGORIES ? 'הכל' : cat }}
        </div>
      </div>

      <div v-if="isReorderMode"
           class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 px-4 md:px-0 mb-3">
        <span class="font-medium">גררו את השירותים לסדר הרצוי</span>
        <div class="flex gap-2 md:ms-auto">
          <Button
            size="small"
            severity="secondary"
            text
            label="ביטול"
            :disabled="reorderSaving"
            @click="cancelReorder" />
          <Button
            size="small"
            icon="pi pi-check"
            label="שמירת סדר"
            :loading="reorderSaving"
            @click="saveReorder" />
        </div>
      </div>

      <div class="flex flex-col w-full mb-2 h-full overflow-y-auto px-4 md:px-0">
        <template v-if="isReorderMode">
          <draggable
            v-model="reorderList"
            item-key="id"
            :animation="180"
            handle=".service-drag-handle"
            class="w-full flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-6"
          >
            <template #item="{ element: service }">
              <div class="service-drag-handle">
                <service-card :service="service" :reorder-mode="true" />
              </div>
            </template>
          </draggable>
          <div v-if="isMobile" class="h-4 p-2 flex-none"></div>
        </template>
        <template v-else>
          <div v-if="servicesToShow.length > 0" class="w-full flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-6">
            <service-card v-for="service of servicesToShow"
                         :key="service.id"
                         :service="service"
                          @clicked-edit="clickedEdit"
            />
            <div v-if="isMobile" class="h-4 p-2 flex-none"></div>
          </div>
          <div v-else-if="searchValue" class="w-full flex flex-col items-center justify-center py-8 text-surface-500">
            <i class="pi pi-search text-3xl mb-3" />
            <span class="text-lg font-medium">לא נמצאו שירותים</span>
            <span class="text-sm">נסו לחפש עם מילות מפתח אחרות</span>
          </div>
          <div v-else class="w-full h-max flex flex-col justify-center items-center my-auto">
            <img
              src="../../../../../assets/img/admin/create-services.svg"
              class="w-44 mb-4"
              alt="יצירת שירות">
            <span class="text-lg font-medium">נראה שעוד לא יצרתם אף שירות</span>
            <span class="text-center px-4 font-light">הוסיפו עכשיו את השירות הראשון שלכם, ואתם מוכנים לצאת לדרך</span>
            <div
              class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4"
              @click="addNewService">
              <i class="pi pi-verified" />
              <span class="text-sm">הוספת שירות ראשון</span>
            </div>
            <div
              v-if="!onlyAllowedToChoose"
              class="relative py-2 px-4 m-2 text-primary-600 rounded-3xl flex gap-2 justify-center items-center cursor-pointer"
              @click="openImportServices">
              <i class="pi pi-upload" />
              <span class="text-sm">או ייבאו רשימת שירותים קיימת</span>
            </div>
          </div>
        </template>
      </div>
    </div>

  </div>
</template>

<script lang="ts" setup>
import { IService, ServiceType } from '@torim-app/types';
import { computed, onMounted, ref } from 'vue';
import { useAdminStore } from '@app/stores';
import AddOrEditService from '@app/components/admin/modals/AddOrEditService.vue';
import ChooseExistingService from '@app/components/admin/modals/ChooseExistingService.vue';
import ImportServices from '@app/components/admin/modals/ImportServices.vue';
import { useConfirm } from 'primevue/useconfirm';
import { useDrawer } from '@torim-app/shared-components';
import { useResponsive } from '@app/composables/responsive';
import { trackEvent } from '@app/composables/posthog';
import ServiceCard from '@app/components/admin/components/ServiceCard.vue';
import draggable from 'vuedraggable';
import { ServicesApi } from '@app/api/admin/services-api';
import { useToast } from 'vue-toastification';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';

const adminStore = useAdminStore();
const confirm = useConfirm();
const { openDrawer, closeDrawer } = useDrawer();
const { isMobile } = useResponsive();
const toast = useToast();

const selectedServiceToEdit = ref<IService>();
const selectedProvider = ref<string>(adminStore.userId ?? '');
const searchValue = ref<string>('');
const isReorderMode = ref<boolean>(false);
const reorderList = ref<IService[]>([]);
const reorderSaving = ref<boolean>(false);
const ALL_CATEGORIES = '__all__';
const selectedCategory = ref<string>(ALL_CATEGORIES);

const isMainClient = computed(() => adminStore.getMainClient);
const allServices = computed<Array<IService>>(() => adminStore.getRegularServices ?? []);
const hasAnyServices = computed(() => allServices.value.length > 0);
const categoryChips = computed<string[]>(() => {
  const set = new Set<string>();
  for (const s of allServices.value)
    if (s.category) set.add(s.category);
  return [ALL_CATEGORIES, ...Array.from(set)];
});
const shouldShowCategoryChips = computed(() => categoryChips.value.length > 1);
const servicesToShow = computed(() => {
  let list = allServices.value;
  if (selectedCategory.value !== ALL_CATEGORIES)
    list = list.filter(service => service.category === selectedCategory.value);
  const q = searchValue.value.trim().toLowerCase();
  if (q)
    list = list.filter(service =>
      service.name?.toLowerCase().includes(q) ||
      service.description?.toLowerCase().includes(q) ||
      service.category?.toLowerCase().includes(q)
    );
  return list;
});
const onlyAllowedToChoose = computed(() => {
  return !adminStore.getBusinessSiteData?.configurations?.allowEmployeeToCreateServices && !isMainClient.value;
});

const addNewService = () => {
  trackEvent('add_service_clicked', { source: 'services_page' });
  selectedServiceToEdit.value = undefined;
  if (onlyAllowedToChoose.value) {
    openDrawer(ChooseExistingService, { providerId: selectedProvider.value, onClose: closeDrawer }, { title: 'בחירת שירות' });
  } else {
    openDrawer(AddOrEditService, { serviceType: ServiceType.REGULAR, service: undefined, mode: 'create', onClose: closeDrawer }, { title: 'הוספת שירות חדש' });
  }
};

const clickedEdit = (service: IService) => {
  trackEvent('edit_service_clicked');
  selectedServiceToEdit.value = service;
  openDrawer(AddOrEditService, {  serviceType: ServiceType.REGULAR, service, mode: 'edit', onClose: closeDrawer }, { title: 'עריכת שירות' });
};

const openImportServices = () => {
  trackEvent('import_services_clicked', { source: 'services_page' });
  openDrawer(ImportServices, { onClose: closeDrawer }, { title: 'ייבוא שירותים' });
};

const enterReorderMode = () => {
  trackEvent('services_reorder_started');
  reorderList.value = [...allServices.value];
  isReorderMode.value = true;
};

const cancelReorder = () => {
  isReorderMode.value = false;
  reorderList.value = [];
};

const saveReorder = async () => {
  try {
    reorderSaving.value = true;
    trackEvent('services_reorder_saved');
    await ServicesApi.reorderServices(reorderList.value.map(s => s.id));
    await adminStore.fetchBusinessData();
    toast.success('סדר השירותים נשמר');
    isReorderMode.value = false;
    reorderList.value = [];
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשמור את הסדר, אנא נסו שוב');
  } finally {
    reorderSaving.value = false;
  }
};

onMounted(() => {
  selectedProvider.value = adminStore.getUserId;
});

</script>
